import React, { useEffect, useMemo, useState } from 'react';
import { getJobs } from '../api';
import { API, FILE_BASE } from '../config';
import { APPLICATION_STATUSES } from '../constants/apparel';
import { buildPhotoMap, photoUrl } from '../siteData';
import { Stage, Link, SiteFooter } from '../components/Site';
import ApplyForm from '../components/ApplyForm';

const cap1 = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const expText = (j) => {
  if (j.expMin == null && j.expMax == null) return '';
  if (!j.expMin) return `Up to ${j.expMax} years`;
  return `${j.expMin} to ${j.expMax} years`;
};
const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

function Skeleton() {
  return (
    <div aria-hidden="true">
      {[3, 2].map((n, g) => (
        <div className="dept" key={g}>
          <div className="sk sk-head" />
          {Array.from({ length: n }).map((_, i) => <div className="sk sk-job" key={i} />)}
        </div>
      ))}
    </div>
  );
}

function Detail({ job }) {
  const lines = (job.responsibilities || '').split('\n').map((x) => x.replace(/^[\s•*-]+/, '').trim()).filter(Boolean);
  const facts = [
    ['Experience', expText(job)],
    ['Location', [job.region, job.workMode].filter(Boolean).join(', ')],
    ['Type', job.employmentType],
    ['Shift', job.shiftType],
    ['Education', job.education],
    ['Code', job.jobCode],
    ['Posted', job.createdAt ? fmtDate(job.createdAt) : ''],
    ['Apply by', job.deadline ? fmtDate(job.deadline) : ''],
  ].filter((f) => f[1]);
  return (
    <>
      <div className="facts">{facts.map(([k, v]) => <span key={k}><b>{k}</b> {v}</span>)}</div>
      <h4>About the role</h4>
      <p>{job.summary || 'We are still writing the full description for this role. You can apply below, and we will share it when we reply.'}</p>
      {lines.length > 0 && <><h4>What you will do</h4><ul>{lines.map((x, i) => <li key={i}>{x}</li>)}</ul></>}
      {job.skills && job.skills.length > 0 && <><h4>Skills</h4><div className="tags">{job.skills.map((s) => <span key={s}>{s}</span>)}</div></>}
      {job.perks && job.perks.length > 0 && <><h4>Perks</h4><ul>{job.perks.map((s) => <li key={s}>{s}</li>)}</ul></>}
      {job.interviewStages && job.interviewStages.length > 0 && (
        <><h4>How we hire</h4><p>{job.interviewStages.join(', then ')}.</p></>
      )}
      <div className="apply">
        <ApplyForm jobId={job._id} jobTitle={cap1(job.title)} title="Apply for this role" submitLabel="Send application" />
      </div>
    </>
  );
}

function StatusCheck({ jobs }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ busy: false, error: '', apps: null, email: '' });

  const check = async (ev) => {
    ev.preventDefault();
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e)) { setState({ busy: false, error: 'Please enter a valid email address.', apps: null, email: '' }); return; }
    setState({ busy: true, error: '', apps: null, email: e });
    try {
      const res = await fetch(`${API}/applicants/check?email=${encodeURIComponent(e)}`);
      const data = await res.json();
      const apps = Array.isArray(data) ? data : (data ? [data] : []);
      setState({ busy: false, error: '', apps, email: e });
    } catch {
      setState({ busy: false, error: 'We could not check that just now. Please try again.', apps: null, email: '' });
    }
  };

  return (
    <section className="status" id="status" data-photo="status" aria-labelledby="h-status">
      <h2 id="h-status">Already applied? Check where you are</h2>
      <p className="lead">Enter the email you applied with and we will show your applications.</p>
      <form className="status-form" onSubmit={check} noValidate>
        <div>
          <label className="f" htmlFor="st-email">Email</label>
          <input id="st-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
            aria-invalid={state.error ? 'true' : undefined} aria-describedby="st-err" />
        </div>
        <button className="btn soft" type="submit" disabled={state.busy}>{state.busy ? 'Checking' : 'Check status'}</button>
      </form>
      <span className="err" id="st-err" role="alert">{state.error}</span>
      {state.apps && state.apps.length === 0 && (
        <p className="status-none">We could not find an application for {state.email}. Please check the spelling, or use the email you gave when you applied.</p>
      )}
      {state.apps && state.apps.length > 0 && (
        <ul className="status-list" aria-live="polite">
          {state.apps.map((a) => {
            const info = APPLICATION_STATUSES[a.status] || APPLICATION_STATUSES.pending;
            const job = jobs.find((j) => j._id === a.jobId);
            return (
              <li key={a._id}>
                <div><b>{job ? cap1(job.title) : 'A role at Soch'}</b>{job && job.department ? <span> in {job.department}</span> : null}</div>
                <div className="status-meta"><span className="status-badge">{info.label}</span>{a.appliedAt ? <span>Applied {fmtDate(a.appliedAt)}</span> : null}</div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default function Home() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [banner, setBanner] = useState(null);
  const [q, setQ] = useState('');
  const [dep, setDep] = useState('');
  const [open, setOpen] = useState({});
  const [active, setActive] = useState('intro');

  const load = async () => {
    setLoading(true); setLoadError(false);
    try {
      const list = await getJobs();
      setJobs(list);
      if (list.length) setOpen((o) => (Object.keys(o).length ? o : { [list[0]._id]: true }));
    } catch { setLoadError(true); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const res = await fetch(`${API}/settings/banner`);
        if (res.ok) { const data = await res.json(); if (data && data.isActive && data.imageUrl) setBanner(data); }
      } catch { /* the banner is optional */ }
    })();
  }, []);

  const depts = useMemo(() => [...new Set(jobs.map((j) => j.department).filter(Boolean))], [jobs]);
  const photos = useMemo(() => buildPhotoMap(depts), [depts]);
  const term = q.trim().toLowerCase();

  const groups = useMemo(() => depts.map((d) => ({
    d,
    list: jobs.filter((j) => j.department === d
      && (!dep || d === dep)
      && (!term || [j.title, j.department, j.summary, ...(j.skills || [])].join(' ').toLowerCase().includes(term))),
  })).filter((g) => g.list.length), [depts, jobs, term, dep]);
  const shown = groups.reduce((n, g) => n + g.list.length, 0);

  // The stage follows the reading.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((es) => {
      es.forEach((en) => { if (en.isIntersecting && en.target.dataset.photo) setActive(en.target.dataset.photo); });
    }, { rootMargin: '-45% 0px -45% 0px' });
    document.querySelectorAll('[data-photo]').forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [loading, groups.length, loadError]);

  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const stageKey = photos[active] ? active : 'intro';
  const count = !jobs.length ? '' : (shown === jobs.length && !term && !dep)
    ? `${jobs.length} open ${jobs.length === 1 ? 'role' : 'roles'} in ${depts.length} ${depts.length === 1 ? 'department' : 'departments'}`
    : `${shown} ${shown === 1 ? 'role' : 'roles'} shown of ${jobs.length}`;

  return (
    <div className="split">
      <Stage photos={photos} activeKey={stageKey} wordmarkTo="/" />

      <main className="col" id="top">
        <nav className="nav" aria-label="Page">
          <a href="#roles">Open roles</a>
          <Link to="/apply">Send resume</Link>
          <a href="#life">Life at Soch</a>
          <Link to="/?joining">Joining</Link>
        </nav>

        <section className="intro" data-photo="intro" aria-labelledby="h1">
          <h1 id="h1">Careers at Soch</h1>
          <p className="lead">Come and work with us. We make sarees, kurta sets, salwar suits, dress material and lehengas for women across India. We have 150+ stores, and our head office is in Bengaluru.</p>
          <div className="cta">
            <a className="btn main" href="#roles">See open roles</a>
            <Link className="btn soft" to="/apply">Send us your resume</Link>
          </div>
        </section>

        {/* The campaign banner (Careers settings) comes after the title and buttons, so it never pushes them down. */}
        {banner && (
          <figure className="banner">
            {banner.linkUrl
              ? <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer"><img src={`${FILE_BASE}${banner.imageUrl}`} alt={banner.title || ''} /></a>
              : <img src={`${FILE_BASE}${banner.imageUrl}`} alt={banner.title || ''} />}
            {(banner.title || banner.subtitle) && (
              <figcaption>{banner.title && <b>{banner.title}</b>}{banner.subtitle && <span>{banner.subtitle}</span>}</figcaption>
            )}
          </figure>
        )}

        <section className="roles" id="roles" aria-labelledby="h-roles">
          <h2 id="h-roles">Open roles</h2>
          {loading && <Skeleton />}
          {!loading && loadError && (
            <div className="empty" role="alert">
              <h3>Openings unavailable</h3>
              <p>We could not load the openings. Please try again in a few minutes.</p>
              <div className="cta"><button className="btn main" type="button" onClick={load}>Retry</button></div>
            </div>
          )}
          {!loading && !loadError && jobs.length === 0 && (
            <div className="empty">
              <h3>We have no open roles right now.</h3>
              <p>You can still <Link to="/apply">send us your resume</Link> and we will keep it for the next opening.</p>
            </div>
          )}
          {!loading && !loadError && jobs.length > 0 && (
            <>
              <div className="tools">
                <div>
                  <label className="f" htmlFor="q">Search roles</label>
                  <input type="search" id="q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buyer, content, merchandising" autoComplete="off" />
                </div>
                <div>
                  <label className="f" htmlFor="dep">Department</label>
                  <select id="dep" value={dep} onChange={(e) => setDep(e.target.value)}>
                    <option value="">All departments ({jobs.length})</option>
                    {depts.map((d) => <option key={d} value={d}>{d} ({jobs.filter((j) => j.department === d).length})</option>)}
                  </select>
                </div>
              </div>
              <p className="count" role="status">{count}</p>

              {groups.map(({ d, list }) => (
                <section className="dept" key={d} data-photo={d} aria-label={d}>
                  <div className="dept-head"><h3>{d}</h3><span>{list.length} {list.length === 1 ? 'role' : 'roles'}</span></div>
                  <img className="dept-photo" alt="" loading="lazy" src={photoUrl(photos[d][0])} />
                  {list.map((j) => {
                    const isOpen = !!open[j._id];
                    return (
                      <article className={`job ${isOpen ? 'open' : ''}`} key={j._id}>
                        <button className="hd" type="button" aria-expanded={isOpen} aria-controls={`p-${j._id}`} id={`b-${j._id}`} onClick={() => toggle(j._id)}>
                          <span className="t">{cap1(j.title)}</span>
                          <span className="chev" aria-hidden="true" />
                          <span className="m">{[j.region, j.workMode, j.employmentType].filter(Boolean).join(' · ')}</span>
                        </button>
                        {isOpen && (
                          <div className="body" id={`p-${j._id}`} role="region" aria-labelledby={`b-${j._id}`}><Detail job={j} /></div>
                        )}
                      </article>
                    );
                  })}
                </section>
              ))}
              {shown === 0 && (
                <div className="empty">
                  <h3>We could not find a role for that.</h3>
                  <p>Try a different word, or choose all departments. You can also <Link to="/apply">send us your resume</Link> and we will keep it for the next opening.</p>
                </div>
              )}
            </>
          )}
        </section>

        <StatusCheck jobs={jobs} />

        <section className="resume" id="resume" data-photo="resume" aria-labelledby="h-resume">
          <h2 id="h-resume">Not sure which role fits? Send us your resume</h2>
          <p className="lead">Tell us a little about you and attach your resume. When a role opens that suits you, we will write to you.</p>
          <div className="cta"><Link className="btn main" to="/apply">Send us your resume</Link></div>
        </section>

        <section className="life" id="life" data-photo="life" aria-labelledby="h-life">
          <h2 id="h-life">Life at Soch</h2>
          <p>Soch makes ethnic wear for women, for everyday and for big family days. We have 150+ stores across India and our head office is in Bengaluru. People here care how a thing looks and how it is made.</p>
          <p>Several roles here list an employee discount on Soch, health insurance and a performance bonus. Some list a five-day week. Each role says what it offers, so please read the one you like.</p>
        </section>

        <div data-photo="foot">
          <SiteFooter><p>Already selected? <Link to="/?joining">Open your joining page</Link> with the key from your email.</p></SiteFooter>
        </div>
      </main>
    </div>
  );
}
